import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { AnimatedButton } from '../AnimatedButton';
import { PageContainer } from '../Components/PageContainer';
import styles from './ItineraryList.module.css';
import { ItineraryCard } from './ItineraryCard';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useSelector } from 'react-redux';
import { Itinerary } from 'models/itinerary';
import { useItineraries } from 'components/Map/useItineraries';
import { formattedDate } from '../util';

const getItineraryDateRange = (itinerary: Itinerary, language: string) => {
  if (!itinerary.date_itineraries.length) return '';

  if (itinerary.date_itineraries.length === 1) {
    return formattedDate(itinerary.date_itineraries[0].date, 'MMM D', language);
  }
  return `${formattedDate(itinerary.date_itineraries[0].date, 'MMM D', language)} - ${formattedDate(
    itinerary.date_itineraries[itinerary.date_itineraries.length - 1].date,
    'MMM D',
    language
  )}`;
};

interface ItineraryListProps {
  onCreateItineraryClick: () => void;
  onItineraryClick: (itinerary: Itinerary) => void;
}

export const ItineraryList = ({ onCreateItineraryClick, onItineraryClick }: ItineraryListProps) => {
  const { t, i18n } = useTranslation();

  const { itineraries, deleteItinerary } = useItineraries();

  const digitalMap = useSelector(selectDigitalMap);

  const recommendedItineraries = digitalMap?.recommended_itineraries ?? [];

  // Sort itineraries by first date
  const sortedItineraries = [...itineraries].sort((a, b) => {
    if (!a.date_itineraries.length) return 1; // Move empty dates to end
    if (!b.date_itineraries.length) return -1; // Move empty dates to end

    const dateA = moment(a.date_itineraries[0].date);
    const dateB = moment(b.date_itineraries[0].date);
    return dateA.valueOf() - dateB.valueOf();
  });

  const getItineraryThumbnail = (itinerary: Itinerary) => {
    for (const date of itinerary.date_itineraries) {
      for (const item of date.items) {
        const pin = digitalMap?.pins?.find((pin) => pin.key === item.pin_key);
        const thumbnail = pin?.media_items?.find((item) => item.type === 'IMAGE')?.url;
        if (thumbnail) {
          return thumbnail;
        }
      }
    }
    return '';
  };

  return (
    <PageContainer>
      <div className={styles.pageContent}>
        <h1 className={styles.title}>{t('Itinerary Maker')}</h1>
        <p className={styles.subtitle}>{t('Plan Your Day From Here!')}</p>
        <AnimatedButton
          clickedClassName={styles.clicked}
          className={styles.createButton}
          onClick={onCreateItineraryClick}
        >
          <span className={styles.plusIcon}>+</span>
          {t('Create New Itinerary')}
        </AnimatedButton>

        {sortedItineraries.length > 0 && (
          <>
            <h2 className={styles.sectionTitle}>{t('My Itineraries')}</h2>
            <div className={styles.itineraryList}>
              {sortedItineraries.map((itinerary, index) => (
                <ItineraryCard
                  onClick={() => onItineraryClick(itinerary)}
                  key={index}
                  itineraryId={itinerary.id}
                  image={getItineraryThumbnail(itinerary)}
                  title={itinerary.title}
                  date={getItineraryDateRange(itinerary, i18n.language)}
                  onDelete={() => deleteItinerary(itinerary.id)}
                  showShareButton
                />
              ))}
            </div>
          </>
        )}

        {recommendedItineraries.length > 0 && (
          <>
            <h2 className={styles.sectionTitle}>{t('Recommended Itineraries')}</h2>
            <div className={styles.itineraryList}>
              {recommendedItineraries.map((itinerary, index) => (
                <ItineraryCard
                  onClick={() => onItineraryClick(itinerary)}
                  key={index}
                  itineraryId={itinerary.id}
                  image={getItineraryThumbnail(itinerary)}
                  title={itinerary.title}
                  date={getItineraryDateRange(itinerary, i18n.language)}
                  showShareButton
                  isRecommended
                />
              ))}
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};
